import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { Subscription, filter, of, take, timeout } from 'rxjs';
import { TabMenuItem } from '../../../core/models/menu/tab-menu-item';
import { Role } from '../../../core/enum/role.enum';

/** Above this width the desktop sidebar takes over (FRC-749 breakpoint decision). */
const DESKTOP_QUERY = '(min-width: 1025px)';

/**
 * Marker on the history entry pushed while a sheet is open, so BACK closes the sheet. Its value is
 * the open group's name, so FORWARD back onto the entry can reopen that sheet.
 */
const SHEET_HISTORY_STATE = 'investorBottomSheet';

/** A same-document back traversal normally fires popstate within a frame or two. */
const SELF_POP_TIMEOUT_MS = 1000;

/**
 * FRC-749: investor dashboard navigation for ≤1024px (phones, tablets incl. iPad landscape).
 *
 * A fixed bottom tab bar — Dashboard, Wallet, Equity, Token (all from `investorDashboardMenu`,
 * the same config as the desktop sidebar) + Profile (`investorDashboardProfileMenu`). Leaf tabs
 * navigate; group tabs open a bottom sheet listing the group's items.
 *
 * Sheet contract: role=dialog + aria-modal, labelled by its title, focus trapped (cdkTrapFocus,
 * which also returns focus to the originating tab), body scroll locked, closes on X / scrim /
 * Esc / browser BACK. BACK works by pushing one same-URL history entry on open:
 *  - BACK pops it → popstate → close. Nothing else to clean up.
 *  - X / scrim / Esc / crossing into desktop → close and `history.back()` to drop the entry.
 *  - FORWARD onto the entry again (it stays in the forward stack after a close) → reopen its sheet,
 *    so the entry is never a no-op step.
 *  - Selecting an item → navigate with `replaceUrl`, which replaces the pushed entry with the
 *    target, so history ends up exactly as if the tab had linked there directly.
 */
@Component({
  selector: 'app-investor-bottom-nav',
  templateUrl: './investor-bottom-nav.component.html',
  styleUrls: ['./investor-bottom-nav.component.scss'],
  standalone: false,
})
export class InvestorBottomNavComponent implements OnInit, OnDestroy {
  /** `investorDashboardMenu` — Dashboard, Wallet, Equity, Token. */
  @Input() tabs: TabMenuItem[] = [];
  /** `investorDashboardProfileMenu` — appended as the fifth tab. */
  @Input() profileMenu: TabMenuItem;
  @Input() role: Role;
  /** Name of the menu item matching the current page (computed by the layout). */
  @Input() selectedMenu: string;
  /** Things To Do count for the Dashboard tab; hidden when null/0. */
  @Input() todoCount: number | null = null;
  @Input() profileImage: string;

  @Output() logout = new EventEmitter<void>();

  readonly sheetTitleId = 'investor-bottom-sheet-title';

  openGroup: TabMenuItem | null = null;

  private readonly isBrowser: boolean;
  private desktopQuery?: MediaQueryList;
  /** Set while we are popping our own history entry, so that popstate is not treated as BACK. */
  private pendingSelfPop: (() => void) | null = null;
  /** Fallback that finishes a self-pop if its popstate never arrives, so the bar cannot stay inert. */
  private selfPopTimer?: ReturnType<typeof setTimeout>;
  /** Waits for the router to settle after a self-pop before Log Out opens its confirm. */
  private routerSettleSub?: Subscription;
  private hasHistoryEntry = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    if (!this.isBrowser) return;

    this.window.addEventListener('popstate', this.onPopState);
    // Lets app-level floating controls (scroll-to-top) sit above the bar — see src/styles.scss.
    this.renderer.addClass(this.document.body, 'investor-bottom-nav-present');

    this.desktopQuery = this.window.matchMedia?.(DESKTOP_QUERY);
    this.desktopQuery?.addEventListener?.('change', this.onDesktopQueryChange);
  }

  ngOnDestroy() {
    if (!this.isBrowser) return;

    this.window.removeEventListener('popstate', this.onPopState);
    this.desktopQuery?.removeEventListener?.('change', this.onDesktopQueryChange);
    this.renderer.removeClass(this.document.body, 'investor-bottom-nav-present');
    // Only undo our own lock: an ng-bootstrap modal may own body overflow right now.
    if (this.openGroup) this.setScrollLock(false);
    clearTimeout(this.selfPopTimer);
    this.routerSettleSub?.unsubscribe();
  }

  /**
   * True between a dismiss's `history.back()` and its popstate (it is asynchronous). The bar ignores
   * taps meanwhile: a tap in that window would navigate or push an entry that the queued back
   * traversal then undoes. pointer-events only, so focus can still return to the tab.
   */
  get isClosing(): boolean {
    return !!this.pendingSelfPop;
  }

  /** The five tabs, in order, honouring each item's `show(role)`. */
  get visibleTabs(): TabMenuItem[] {
    const tabs = [...(this.tabs ?? []), ...(this.profileMenu ? [this.profileMenu] : [])];
    return tabs.filter((tab) => this.isVisible(tab));
  }

  isVisible(item: TabMenuItem): boolean {
    return !item.show || item.show(this.role);
  }

  visibleItems(group: TabMenuItem): TabMenuItem[] {
    return (group?.submenus ?? []).filter((item) => this.isVisible(item));
  }

  isGroup(tab: TabMenuItem): boolean {
    return !!tab?.submenus?.length;
  }

  isProfileTab(tab: TabMenuItem): boolean {
    return tab === this.profileMenu;
  }

  isItemSelected(item: TabMenuItem): boolean {
    return (
      !!this.selectedMenu && !item.isExternal && !item.action && item.name === this.selectedMenu
    );
  }

  isTabActive(tab: TabMenuItem): boolean {
    return this.isGroup(tab)
      ? this.visibleItems(tab).some((item) => this.isItemSelected(item))
      : this.isItemSelected(tab);
  }

  badgeFor(tab: TabMenuItem): number | null {
    return tab.url === '/dashboard' && this.todoCount ? this.todoCount : null;
  }

  openSheet(group: TabMenuItem) {
    if (!this.isBrowser || this.openGroup) return;

    this.openGroup = group;
    this.setScrollLock(true);

    const history = this.window.history;
    history.pushState({ ...history.state, [SHEET_HISTORY_STATE]: group.name }, '');
    this.hasHistoryEntry = true;
  }

  /** The bar stays usable while a sheet is open: same tab closes it, another group swaps in place. */
  onGroupTabClick(group: TabMenuItem, trigger: HTMLElement) {
    // Keyboard activation still reaches here while the bar ignores pointer events.
    if (this.isClosing) return;

    if (this.openGroup === group) {
      this.dismissSheet();
      return;
    }

    // cdkTrapFocusAutoCapture remembers whatever is focused when the sheet mounts and restores it
    // on close, so focus the tapped tab first (Safari does not focus buttons on click).
    if (this.openGroup) {
      // Swap: re-mount the sheet so the trap recaptures. Focus moves back inside the sheet (Tab and
      // Esc keep working) and closing returns it to this tab, not the one that first opened it.
      this.openGroup = null;
      this.cdr.detectChanges();
      trigger.focus();
      this.openGroup = group;
      // Keep the pushed entry pointing at the sheet now showing, for FORWARD.
      if (this.hasHistoryEntry) {
        const history = this.window.history;
        history.replaceState({ ...history.state, [SHEET_HISTORY_STATE]: group.name }, '');
      }
    } else {
      trigger.focus();
      this.openSheet(group);
    }
  }

  /** Dashboard / Wallet tapped with a sheet open: routerLink navigates with replaceUrl, consuming the pushed entry. */
  onDirectTabClick(event: MouseEvent, tab: TabMenuItem) {
    if (!this.openGroup) return;
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
      return;

    if (this.isCurrentUrl(tab.url)) {
      this.dismissSheet();
      return;
    }

    this.closeSheet();
    this.hasHistoryEntry = false;
  }

  /** X, scrim, Esc, Log Out, crossing into desktop: close and drop the pushed history entry. */
  dismissSheet(afterClose?: () => void) {
    if (!this.openGroup) return;

    this.closeSheet();

    if (this.hasHistoryEntry) {
      this.hasHistoryEntry = false;
      this.pendingSelfPop = afterClose ?? (() => {});
      this.selfPopTimer = setTimeout(() => this.finishSelfPop(), SELF_POP_TIMEOUT_MS);
      this.window.history.back();
    } else {
      afterClose?.();
    }
  }

  /** Our own entry has been popped (or the fallback fired): re-enable the bar, run the follow-up. */
  private finishSelfPop() {
    clearTimeout(this.selfPopTimer);
    const afterClose = this.pendingSelfPop;
    this.pendingSelfPop = null;
    afterClose?.();
  }

  /**
   * Run `fn` once the router has finished the navigation a self-pop starts: the popstate makes it
   * re-run a same-URL navigation a task later (onSameUrlNavigation: 'reload'), and AppComponent
   * dismisses open modals on every NavigationEnd. Falls back after SELF_POP_TIMEOUT_MS.
   */
  private afterRouterSettles(fn: () => void) {
    this.routerSettleSub?.unsubscribe();
    this.routerSettleSub = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError ||
            event instanceof NavigationSkipped,
        ),
        take(1),
        timeout({ first: SELF_POP_TIMEOUT_MS, with: () => of(null) }),
      )
      .subscribe(() => fn());
  }

  onItemClick(event: MouseEvent, item: TabMenuItem) {
    if (item.action === 'logout') {
      event.preventDefault();
      // The confirm popup must open only after our history entry is popped (PopupService dismisses
      // all modals on popstate) and after the router's resulting same-URL navigation has ended
      // (AppComponent dismisses modals on NavigationEnd) — otherwise it flashes and closes.
      this.dismissSheet(() => this.afterRouterSettles(() => this.logout.emit()));
      return;
    }

    // Modified clicks (new tab/window) leave this page — and the sheet — alone.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    if (item.isExternal) {
      event.preventDefault();
      this.closeSheet();
      this.hasHistoryEntry = false;
      // Replace the pushed entry, mirroring the routerLink replaceUrl below.
      this.window.location.replace(item.url);
      return;
    }

    if (this.isCurrentUrl(item.url)) {
      // routerLink still re-runs the same-URL navigation (onSameUrlNavigation: 'reload'), which
      // would leave the pushed entry behind; history.back() drops it and lands on the same URL.
      event.preventDefault();
      this.dismissSheet();
      return;
    }

    // routerLink navigates with replaceUrl, consuming the pushed entry.
    this.closeSheet();
    this.hasHistoryEntry = false;
  }

  private closeSheet() {
    this.openGroup = null;
    this.setScrollLock(false);
  }

  /**
   * Exact match, query and fragment included. preventDefault() does not stop routerLink, so on
   * e.g. /pstx/account?topup=true a Wallet tap must take the normal replaceUrl path (same result
   * as with no sheet open) rather than race its navigation against dismissSheet()'s history.back().
   */
  private isCurrentUrl(url: string): boolean {
    return this.router.url === url;
  }

  private readonly onPopState = () => {
    this.zone.run(() => {
      if (this.pendingSelfPop) {
        this.finishSelfPop();
        return;
      }

      // Device / browser BACK while a sheet is open: the pushed entry is gone, just close.
      if (this.openGroup) {
        this.hasHistoryEntry = false;
        this.closeSheet();
        return;
      }

      // FORWARD onto an entry this component pushed: reopen that sheet (it now owns the entry
      // again). Not on desktop, where the bar is hidden and the scroll lock would strand the page.
      const group = this.groupFromHistoryState();
      if (group && !this.desktopQuery?.matches) {
        this.openGroup = group;
        this.setScrollLock(true);
        this.hasHistoryEntry = true;
      }
    });
  };

  private groupFromHistoryState(): TabMenuItem | undefined {
    const name = this.window.history.state?.[SHEET_HISTORY_STATE];
    if (typeof name !== 'string') return undefined;
    return this.visibleTabs.find((tab) => this.isGroup(tab) && tab.name === name);
  }

  private get window(): Window {
    return this.document.defaultView;
  }

  private readonly onDesktopQueryChange = (event: MediaQueryListEvent) => {
    if (event.matches) {
      this.zone.run(() => this.dismissSheet());
    }
  };

  private setScrollLock(locked: boolean) {
    if (!this.isBrowser) return;
    const body = this.document.body;
    // The body class also hides app-level floating controls (scroll-to-top) — see src/styles.scss.
    if (locked) {
      this.renderer.setStyle(body, 'overflow', 'hidden');
      this.renderer.addClass(body, 'investor-sheet-open');
    } else {
      this.renderer.removeStyle(body, 'overflow');
      this.renderer.removeClass(body, 'investor-sheet-open');
    }
  }
}
